'use client'

import { Slot } from 'radix-ui'
import { Children, cloneElement, useContext, useRef } from 'react'
import { IconButton } from '../IconButton/IconButton'
import { ImageGalleryContext } from './context/ImageGalleryContext'
import type { IconButtonProps } from '../IconButton/IconButton'
import { cn, warnIfEmptyLabel } from '@/utils'
import { Chevron } from '@/icons'

const SCROLL_STEP = 300

export type ImageGalleryProps = Omit<
  React.ComponentProps<'div'>,
  'aria-label'
> & {
  /**
   * Accessible name for the image gallery region. Required — provide a
   * localized string from the consuming app.
   */
  ariaLabel: string

  /**
   * Index of the image shown in the slider. Pass it together with
   * `onIndexChange` to drive the gallery from outside; omit both to let the
   * gallery track the index itself.
   */
  activeIndex?: number

  /**
   * Called with the new index whenever the slider or a thumbnail changes the
   * visible image.
   */
  onIndexChange?: (index: number) => void

  /**
   * Defines the behavior of the hover event on thumbnails
   * - `select` - Selects the thumbnail on hover
   * - `preview` - Shows the preview on hover
   * - `none` - No action on hover
   *
   * @default 'select'
   */
  thumbnailHoverMode?: 'select' | 'preview' | 'none'
}

const ImageGallery = ({
  children,
  className,
  activeIndex,
  onIndexChange,
  thumbnailHoverMode = 'select',
  ariaLabel,
  ...props
}: ImageGalleryProps) => {
  const thumbnailsScrollRef = useRef<HTMLUListElement>(null)

  warnIfEmptyLabel(ariaLabel, 'ImageGallery: ariaLabel')

  const carouselArrowClassName = cn(
    '!size-[24px] shrink-0 hidden', // arrows are hidden on mobile devices
    'md:max-2xl:group-has-[ul>li:nth-child(n+5)]:flex', // shows arrow if 5 or more items
    '2xl:group-has-[ul>li:nth-child(n+6)]:flex', // show arrow if 6 or more items
  )

  return (
    <ImageGalleryContext
      value={{ activeIndex, onIndexChange, thumbnailHoverMode }}
    >
      <section
        className={cn(
          'ImageGallery__root',
          'relative flex w-full md:overflow-hidden',
          className,
        )}
        aria-label={ariaLabel}
        aria-live="polite"
        {...props}
      >
        <div className="group flex w-full items-center gap-2 self-end md:h-full md:w-auto md:flex-col md:self-start">
          <div className={carouselArrowClassName}>
            <ImageGalleryArrow
              className={cn('-rotate-90 md:rotate-0')}
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => {
                thumbnailsScrollRef.current?.scrollBy({
                  top: SCROLL_STEP * -1,
                  left: SCROLL_STEP * -1,
                  behavior: 'smooth',
                })
              }}
            />
          </div>

          <ul
            className={cn(
              'ImageGallery__thumbnails',
              'flex overflow-y-auto md:block',
              'space-x-1 space-y-2 md:space-x-[0]',
              '[&::-webkit-scrollbar]:hidden',
            )}
            ref={thumbnailsScrollRef}
          >
            {Children.map(children, (child, index) =>
              cloneElement(child as React.ReactElement<{ index: number }>, {
                index,
              }),
            )}
          </ul>

          <div className={carouselArrowClassName}>
            <ImageGalleryArrow
              className={cn('rotate-90 md:rotate-180', carouselArrowClassName)}
              aria-hidden="true"
              tabIndex={-1}
              onClick={() => {
                thumbnailsScrollRef.current?.scrollBy({
                  top: SCROLL_STEP,
                  left: SCROLL_STEP,
                  behavior: 'smooth',
                })
              }}
            />
          </div>
        </div>
      </section>
    </ImageGalleryContext>
  )
}

const ImageGalleryItem = ({
  children,
  className,
  index,
  ...props
}: React.ComponentProps<'li'> & { index?: number }) => (
  <li
    className={cn(
      'ImageGallery__item',
      'flex h-[105px] w-[72px] items-center justify-center overflow-hidden xl:h-[151px] xl:w-[104px]',
      className,
    )}
    style={{ flex: `0 0 72px` }}
    data-index={index}
    {...props}
  >
    {Children.map(children, (child) =>
      cloneElement(child as React.ReactElement<{ index: number }>, { index }),
    )}
  </li>
)

const ImageGalleryThumbnail = ({
  children,
  className,
  index,
  onMouseEnter,
  onKeyDown,
  ...props
}: React.ComponentProps<'label'> & { index?: number }) => {
  const checkboxRef = useRef<HTMLInputElement>(null)

  const { activeIndex, onIndexChange, thumbnailHoverMode } =
    useContext(ImageGalleryContext)

  const getId = () => {
    return props.id || `image-gallery-thumbnail-${index}`
  }

  const getDefaultChecked = () => {
    if (activeIndex === undefined) return index === 0

    return undefined
  }

  const handleActiveIndexChange = () => {
    if (checkboxRef.current?.checked) return

    onIndexChange?.(index!)
  }

  return (
    <>
      <input
        type="radio"
        className="peer/state hidden"
        name="image-gallery-thumbnail"
        ref={checkboxRef}
        id={getId()}
        defaultChecked={getDefaultChecked()}
        checked={
          activeIndex === undefined || getDefaultChecked()
            ? undefined
            : activeIndex === index
        }
        readOnly
      />

      <label
        className={cn(
          'ImageGallery__thumbnail',
          '[&_img]>object-cover peer/thumb h-full transition-opacity duration-100',
          'flex-[1] cursor-pointer hover:opacity-60 peer-checked/state:opacity-60',
          className,
        )}
        tabIndex={0}
        htmlFor={getId()}
        onMouseEnter={(...args) => {
          if (thumbnailHoverMode === 'select') {
            handleActiveIndexChange()
            checkboxRef.current!.checked = true
          }

          onMouseEnter?.(...args)
        }}
        onKeyDown={(e) => {
          handleActiveIndexChange()
          onKeyDown?.(e)

          if (e.key === 'Enter' || e.key === ' ') {
            checkboxRef.current!.checked = true
            e.preventDefault()
          }
        }}
        {...props}
      >
        {children}
      </label>
    </>
  )
}

const ImageGalleryPreview = ({
  asChild,
  disableImageCover,
  className,
  ...props
}: React.ComponentProps<'div'> & {
  disableImageCover?: boolean
  asChild?: boolean
}) => {
  const { thumbnailHoverMode } = useContext(ImageGalleryContext)

  const Component = asChild ? Slot.Root : 'div'

  return (
    <Component
      className={cn(
        'ImageGallery__preview',
        'absolute top-0 hidden items-center justify-center',
        'left-0 h-[calc(100%-130px)] w-full md:left-[122px] md:h-full md:w-[calc(100%-122px)]',
        'peer-checked/state:flex',
        {
          '[&_img]:h-full [&_img]:object-cover [&_picture]:h-full':
            !disableImageCover,
          'peer-hover/thumb:z-[2] peer-hover/thumb:flex':
            thumbnailHoverMode !== 'none',
        },
        className,
      )}
      {...props}
    />
  )
}

const ImageGalleryArrow = ({ ...props }: IconButtonProps) => (
  <IconButton size="sm" {...props}>
    <Chevron className="!size-[10px]" />
  </IconButton>
)

export {
  ImageGallery,
  ImageGalleryItem,
  ImageGalleryPreview,
  ImageGalleryThumbnail,
}
